// EVERY DIALOG IN THE APP IS THIS ONE (QA 2026-09-30, C1-10; `docs/decisions.md`
// D-239).
//
// Fluent's `Dialog` keeps a closed dialog mounted until its exit animation
// reports that it has finished: `useDialog` builds the surface's presence motion
// with `visible: open, unmountOnExit: true`, and `createPresenceComponent`
// unmounts only in the animation's `onfinish` callback. Until then the backdrop
// and the surface stay in the page, and neither stops taking pointer events.
// The animation is 250ms on paper, but its finish is a Web Animations callback,
// so it lands only when the browser gets round to it: the QA tester measured the
// "Are you sure?" backdrop still over the invoice modal 1.6 to 1.9 seconds after
// Cancel, and a click on Release in that window hit the backdrop and was lost.
// The closing surface also kept focus, and its Escape handler marks the key
// handled even while closed, so an Escape in that window was swallowed.
//
// `surfaceMotion={null}` is Fluent's own switch for no surface motion: with it,
// `presenceMotionSlot` renders the dialog while `open` and nothing once it is
// false, in the same render. So a closed dialog leaves the page at once: no
// backdrop to click on, no surface to hold focus. (Where focus goes next is
// the restore below, C1-11.) The cost is the surface's scale-in and fade-out;
// the backdrop still fades in.
//
// IT DEPENDS ON `unmountOnClose` STAYING AT ITS DEFAULT, `true`. With `false`,
// `presenceMotionSlot` keeps rendering a closed dialog (only `unmountOnExit`
// lets it return nothing), so with the motion off the dialog would stay on
// screen, backdrop and all, after it closed. Never pass
// `unmountOnClose={false}` to an `AppDialog`; the sweep test fails if one does.
//
// FOCUS GOES BACK TO THE CONTROL THAT OPENED IT (QA 2026-09-30, C1-11;
// D-240). Fluent restores focus only to an element tabster has seen focused
// with a restore-target attribute, which `DialogTrigger` adds, and no dialog
// here opens from one, so every close left focus on `document.body`. That was
// true before D-239 as well: the closing surface only held focus until its exit
// animation ended. With focus on the body, an Escape after cancelling an "Are
// you sure?" never reached the invoice modal. So this component records the
// element that had focus as the dialog opened, in a layout effect (layout
// effects run before Fluent's `useFocusFirstElement` effect moves focus into
// the dialog), and puts focus back there as it closes, when `restoreTarget`
// (`lib/dialogFocus.ts`) says to: the opener is still in the page and focus was
// lost to the body.
//
// Use it wherever a `Dialog` would go. `line-item-columns-and-step-fixes.test.ts` fails if a
// screen renders Fluent's `Dialog` directly.
//
// EVERY DIALOG DIMS THE PAGE BEHIND IT, NESTED OR NOT (owner, 2026-09-30;
// D-244): "When any pop-up is visible, I would expect the background to be
// dimmed, while that pop-up is the focus and the only thing with which the user
// is interacting." Fluent dims the page only behind a dialog opened from the
// page. A dialog opened from inside another dialog (the duplicate comparison
// over the invoice modal, the second press over the vendor import) gets a
// TRANSPARENT backdrop: it still blocks clicks, but the dialog under it looks
// as live as the new one. `DecisionDialog` fixed that for itself on 2026-09-25
// with `backdrop={{ appearance: 'dimmed' }}`; this component now sets the same
// on every `DialogSurface` handed to it, keeping any other backdrop props the
// caller passed (the Upload pop-up's file-drop guard). A caller's own
// `appearance` is overridden on purpose, so no dialog can opt out.
//
// Modality itself is Fluent's default, `modalType="modal"`: the backdrop, the
// focus trap, the page behind hidden from a screen reader, and the page scroll
// locked. No dialog passes `modalType`; the Upload pop-up was the one
// non-modal dialog until D-244, and `popups-modal.test.ts` fails if one does.
//
// A CLICK AWAY FROM AN OPEN DROPDOWN CLOSES THE DROPDOWN, NOT THE POP-UP, AND
// A CLICK ON AN OPEN DROPDOWN'S LABEL CLOSES IT (owner, 2026-10-12 — D-351).
// Both are handled here, once, for every dropdown in every pop-up; the
// mechanism and the rules are in `lib/dialogPopups.ts`.
import { cloneElement, useLayoutEffect, useRef, type ReactElement } from 'react'
import { Dialog, DialogSurface, type DialogProps, type DialogSurfaceProps } from '@fluentui/react-components'
import { restoreTarget } from '../lib/dialogFocus'
import { OPEN_POPUP_SELECTOR, isPopupOpen, keepDialogOpen, labelledPopupControl } from '../lib/dialogPopups'

type DialogChild = ReactElement

/** The surface with its backdrop forced to `dimmed`; any other child unchanged. */
function dimmed(child: DialogChild): DialogChild {
  if (child?.type !== DialogSurface) return child
  const surface = child as ReactElement<DialogSurfaceProps>
  const own = surface.props.backdrop
  // Only a props object is kept. A backdrop given as an element, a string or
  // `null` (which would remove it) is replaced by the plain dimmed one.
  const kept = own !== null && typeof own === 'object' && !('$$typeof' in own) ? own : {}
  return cloneElement(surface, { backdrop: { ...kept, appearance: 'dimmed' } })
}

function restoreFocus(opener: HTMLElement | null): void {
  const target = restoreTarget(opener, document.activeElement, document.body)
  target?.focus({ preventScroll: true })
}

export function AppDialog(callerProps: DialogProps) {
  const { children, onOpenChange } = callerProps
  // The list control the latest press found open, if any (D-351).
  const popupOpenAtPress = useRef<HTMLElement | null>(null)
  const props: DialogProps = {
    ...callerProps,
    children: Array.isArray(children) ? [dimmed(children[0]), dimmed(children[1])] : dimmed(children),
    onOpenChange: (event, data) => {
      const pressedOpen = popupOpenAtPress.current
      if (keepDialogOpen(data.type, pressedOpen !== null)) {
        // The press on the backdrop took focus out of the dialog; give it back
        // to the control whose list just closed, so Escape and Tab still work.
        pressedOpen?.focus({ preventScroll: true })
        return
      }
      onOpenChange?.(event, data)
    },
  }
  const open = callerProps.open === true
  const opener = useRef<HTMLElement | null>(null)
  const wasOpen = useRef(false)

  // Captured on the document so they run before Fluent's own handlers: the
  // press is when Fluent closes a list, so it is the only moment that can say
  // whether one was open. A layout effect, like the two below, so they are in
  // place before the first press can land (and this file has no passive
  // effect, which `line-item-columns-and-step-fixes.test.ts` holds it to).
  useLayoutEffect(() => {
    if (!open) return
    let openLabelledControl: HTMLElement | null = null
    const onPointerDown = (event: PointerEvent) => {
      popupOpenAtPress.current = document.querySelector<HTMLElement>(OPEN_POPUP_SELECTOR)
      const control = labelledPopupControl<HTMLElement>(event.target)
      openLabelledControl = isPopupOpen(control) ? control : null
    }
    const onClick = (event: MouseEvent) => {
      const control = labelledPopupControl<HTMLElement>(event.target)
      if (control !== null && control === openLabelledControl) {
        // The press already closed the list; stop the label reopening it.
        event.preventDefault()
        control.focus({ preventScroll: true })
      }
      openLabelledControl = null
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('click', onClick, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('click', onClick, true)
    }
  }, [open])

  useLayoutEffect(() => {
    if (open && !wasOpen.current) {
      const active = document.activeElement
      opener.current = active instanceof HTMLElement ? active : null
    } else if (!open && wasOpen.current) {
      restoreFocus(opener.current)
      opener.current = null
    }
    wasOpen.current = open
  }, [open])

  // A dialog removed while still open (its screen unmounted) gives focus back too.
  useLayoutEffect(
    () => () => {
      if (wasOpen.current) restoreFocus(opener.current)
    },
    [],
  )

  return <Dialog {...props} surfaceMotion={null} />
}
